import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Cloud, Key, DollarSign, AlertTriangle, Clock, ChevronRight, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getLogoByIntegrationId } from '../constants/logos';

const accessCases = [
  {
    id: "case_saas_001",
    type: "access_request",
    typeLabel: "Access Request",
    subject: { name: "Mike Lee", app: "Salesforce", tier: "Admin", logoId: "salesforce" },
    status: "pending_approval",
    priority: "p2",
    reason: "Q1 sales reporting",
    approvalNeeded: "Manager + App Owner",
    agents: ["saas", "identity"],
    sla: { remaining: "3h", breached: false }
  },
  {
    id: "case_saas_002",
    type: "access_review",
    typeLabel: "Access Review",
    subject: { name: "Figma", description: "Quarterly access review", logoId: "figma" },
    status: "executing",
    priority: "p3",
    progress: { reviewed: 45, total: 128, flagged: 12 },
    agents: ["saas"],
    sla: { remaining: "5d", breached: false }
  },
  {
    id: "case_saas_003",
    type: "stale_access",
    typeLabel: "Stale Access Removal",
    subject: { name: "8 users", app: "GitHub Enterprise", inactive: "90+ days", logoId: "github" },
    status: "pending_approval",
    priority: "p3",
    recommendation: "Revoke access and reclaim seats",
    savings: "$800/month",
    agents: ["saas"],
    sla: { remaining: "7d", breached: false }
  }
];

const spendCases = [
  {
    id: "case_spend_001",
    type: "license_reclaim",
    typeLabel: "License Reclamation",
    subject: { app: "Figma", seats: 12, inactive: "90+ days", logoId: "figma" },
    status: "pending_approval",
    priority: "p3",
    savings: "$180/month",
    recommendation: "Propose reclamation bundle",
    agents: ["saas"]
  },
  {
    id: "case_spend_002",
    type: "renewal_optimization",
    typeLabel: "Renewal Optimization",
    subject: { app: "Slack", renewal: "2026-02-15", currentSpend: "$45,000/yr", logoId: "slack" },
    status: "pending_approval",
    priority: "p2",
    recommendation: "Right-size to Business+ tier",
    savings: "$12,000/year",
    agents: ["saas"]
  },
  {
    id: "case_spend_003",
    type: "duplicate_app",
    typeLabel: "Duplicate Detection",
    subject: { apps: ["Miro", "FigJam", "Lucidchart"], users: 89, logoId: "whiteboard" }, // No generic logo for whiteboard group yet
    status: "pending_approval",
    priority: "p3",
    recommendation: "Consolidate to single whiteboard tool",
    savings: "$8,500/year",
    agents: ["saas"]
  }
];

const saasMetrics = {
  totalApps: 47,
  totalSpend: "$425K/yr",
  unusedLicenses: 156,
  potentialSavings: "$52K/yr",
  accessReviewsPending: 3,
  avgProvisionTime: "12m"
};

export const SaaSPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('access');

  const handleProposeBundle = () => {
    navigate('/case/case_saas_new/review');
  };

  const getStatusBadge = (status) => {
    const config = {
      executing: 'badge-executing',
      pending_approval: 'badge-pending',
      verifying: 'badge-verifying',
      closed: 'badge-active'
    };
    const labels = {
      executing: 'Executing',
      pending_approval: 'Needs Approval',
      verifying: 'Verifying',
      closed: 'Completed'
    };
    return { className: config[status] || 'badge-neutral', label: labels[status] || status };
  };

  return (
    <div className="space-y-6" data-testid="saas-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Cloud className="w-5 h-5 text-slate-500" />
            SaaS Governance
          </h1>
          <p className="text-sm text-slate-500 mt-1">Access control and spend optimization</p>
        </div>
        <Button onClick={handleProposeBundle} size="sm" data-testid="propose-bundle-btn">
          <Plus className="w-4 h-4 mr-1" />
          Propose Action Bundle
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Total Apps</span>
            <Cloud className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{saasMetrics.totalApps}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Annual Spend</span>
            <DollarSign className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{saasMetrics.totalSpend}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Unused Licenses</span>
            <AlertTriangle className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{saasMetrics.unusedLicenses}</div>
          <div className="text-xs text-primary mt-1">{saasMetrics.potentialSavings} recoverable</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Provision Time</span>
            <Clock className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{saasMetrics.avgProvisionTime}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="border-b border-slate-100">
          <div className="flex">
            <button
              onClick={() => setActiveTab('access')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'access'
                ? 'text-primary border-b-2 border-primary'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              Access Governance
            </button>
            <button
              onClick={() => setActiveTab('spend')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'spend'
                ? 'text-primary border-b-2 border-primary'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              Spend Optimization
            </button>
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {(activeTab === 'access' ? accessCases : spendCases).map((caseItem) => {
            const statusBadge = getStatusBadge(caseItem.status);
            return (
              <div
                key={caseItem.id}
                className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/case/${caseItem.id}`)}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${activeTab === 'access' ? 'bg-slate-100' : 'bg-primary/10'}`}>
                    {caseItem.subject.logoId && getLogoByIntegrationId(caseItem.subject.logoId) ? (
                      <img
                        src={getLogoByIntegrationId(caseItem.subject.logoId).src}
                        alt={getLogoByIntegrationId(caseItem.subject.logoId).alt}
                        className="w-5 h-5 object-contain"
                      />
                    ) : activeTab === 'access' ? (
                      <Key className="w-4 h-4 text-slate-600" />
                    ) : (
                      <DollarSign className="w-4 h-4 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`badge-status ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                      <span className="text-sm font-medium text-slate-900">{caseItem.typeLabel}</span>
                    </div>
                    <p className="text-sm text-slate-600 truncate">
                      <span className="font-medium text-slate-900">{caseItem.subject.name || caseItem.subject.app}</span>
                      {caseItem.subject.tier && <span className="text-slate-500"> - {caseItem.subject.tier} access</span>}
                      {caseItem.subject.seats && <span className="text-slate-500"> - {caseItem.subject.seats} seats</span>}
                    </p>

                    {caseItem.progress && (
                      <div className="mt-2 max-w-sm">
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                          <span>{caseItem.progress.reviewed} of {caseItem.progress.total} reviewed</span>
                          {caseItem.progress.flagged > 0 && (
                            <span className="text-slate-600">{caseItem.progress.flagged} flagged</span>
                          )}
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(caseItem.progress.reviewed / caseItem.progress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {caseItem.savings && (
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded border border-primary/20">
                        <DollarSign className="w-3 h-3" />
                        {caseItem.savings} potential savings
                      </div>
                    )}

                    {caseItem.recommendation && (
                      <button className="block mt-2 text-xs text-primary hover:underline">
                        Recommended: {caseItem.recommendation}
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {caseItem.sla && (
                      <div className="text-right">
                        <span className="block text-xs text-slate-400">SLA</span>
                        <span className="text-sm font-medium text-slate-700">{caseItem.sla.remaining}</span>
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">SaaS Agent Status</h3>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary"></div>
                Trust: <span className="font-medium text-slate-900">94.1%</span>
              </span>
              <span>Rollback Rate: <span className="font-medium text-slate-900">5.6%</span></span>
              <span className="text-slate-600 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                <img src={getLogoByIntegrationId('slack').src} alt="Slack" className="w-3.5 h-3.5 object-contain" />
                Slack connector degraded
              </span>
            </div>
          </div>
          <Link to="/agents" className="text-xs text-primary hover:underline">
            View Agent Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SaaSPage;
